import { Box, useTheme } from '@mui/material';

import { Typography } from '@zendesk/forethought-elements';
import { NA } from '../constants';
import { getAiScoreMetricColor } from 'src/components/dashboard-pages/agent-insights/utils';
import { Conversation } from 'src/services/apiInterfaces';
import { capitalizeFirstLetter } from 'src/utils/capitalizeFirstLetter';

interface AiQaChatDrawerTabContentProps {
  aiQaScores: NonNullable<Conversation['ai_qa_scores']>;
}

const AiQaChatDrawerTabContent = ({
  aiQaScores,
}: AiQaChatDrawerTabContentProps) => {
  const { palette } = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        paddingTop: '20px',
      }}
    >
      {aiQaScores.map((score, index) => {
        const numericScore = Number(score.score);
        const normalizedScore = Number(score.normalized_score);
        const isNaValue = numericScore === -1;

        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <Typography variant='font14Bold'>{score.rubric_name}</Typography>
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: '12px',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  position: 'relative',
                }}
              >
                {score.performance_goal !== null && (
                  <Box
                    sx={{
                      backgroundColor: palette.colors.grey[800],
                      borderRadius: '4px',
                      fontSize: '10px',
                      left: `${score.performance_goal}%`,
                      padding: '6px 12px',
                      position: 'absolute',
                      top: '-40px',
                      transform: 'translateX(-50%)',
                      whiteSpace: 'nowrap',
                      zIndex: 3,
                    }}
                  >
                    <Typography color={palette.colors.white} variant='font14'>
                      {score.performance_goal}
                    </Typography>
                  </Box>
                )}
                {score.performance_goal !== null && (
                  <Box
                    sx={{
                      bgcolor: palette.colors.grey[700],
                      height: '24px',
                      left: `${score.performance_goal}%`,
                      position: 'absolute',
                      top: '-4px',
                      transform: 'translateX(-50%)',
                      width: '2px',
                      zIndex: 4,
                    }}
                  />
                )}
                <Box
                  sx={{
                    bgcolor: palette.colors.grey[100],
                    borderRadius: '5px',
                    height: '16px',
                    overflow: 'hidden',
                    position: 'relative',
                    width: '100%',
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: getAiScoreMetricColor({
                        isBgColor: true,
                        palette,
                        score: normalizedScore ?? numericScore,
                      }),

                      borderRadius: '5px',
                      height: '100%',
                      width: `${normalizedScore ?? numericScore}%`,
                    }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  flexShrink: 0,
                  overflow: 'hidden',
                  textAlign: 'right',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  width: 80,
                }}
              >
                <Typography
                  color={
                    isNaValue ? palette.colors.grey[600] : palette.colors.black
                  }
                  noWrap
                  variant='font14Medium'
                >
                  {isNaValue
                    ? NA
                    : score.label
                      ? capitalizeFirstLetter(score.label)
                      : numericScore}
                </Typography>
              </Box>
            </Box>
            <Typography
              color={score.reasoning ? undefined : palette.colors.black}
              variant='font12Medium'
            >
              {score.reasoning || NA}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default AiQaChatDrawerTabContent;
